import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { articles, hotRank } from '@/mocks/articles';
import { siteMeta } from '@/mocks/site';
import { formatHeat } from '@/utils/format';

const Hero = () => {
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();
  const featured = articles[0];

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/topics?q=${encodeURIComponent(value)}` : '/topics');
  };

  return (
    <section className="relative overflow-hidden border-b border-background-200/70 bg-gradient-to-br from-background-50 via-background-100 to-secondary-50">
      <div
        className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary-200/40"
        aria-hidden="true"
      ></div>
      <div
        className="pointer-events-none absolute -bottom-32 left-1/4 h-72 w-72 rounded-full bg-accent-200/40"
        aria-hidden="true"
      ></div>

      <div className="relative mx-auto grid max-w-content grid-cols-1 gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-[1.05fr_1fr] lg:items-center lg:py-20">
        <div className="animate-rise">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
            <i className="ri-fire-line text-sm"></i>
            独家内容首发阵地 · 每日更新
          </span>

          <h1 className="mt-5 font-heading text-3xl font-bold leading-tight tracking-tight text-foreground-950 md:text-5xl">
            <span className="font-brand text-primary-600">{siteMeta.name}</span>
            <span className="mt-2 block text-2xl md:text-3xl">{siteMeta.slogan}</span>
          </h1>

          <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
            内部私料、一手信息独一份呈现。别人挖不到的深层内幕，这里给你拆开讲透，
            专供想比旁人先知道真相的你。
          </p>

          <form onSubmit={handleSearch} className="mt-7 max-w-xl">
            <div className="flex items-center rounded-full border border-background-300/70 bg-background-50 p-1.5 pl-4 transition-colors focus-within:border-primary-400">
              <i className="ri-search-line mr-2 text-base text-foreground-500"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索艺人、剧集或行业关键词"
                aria-label="搜索爆料"
                className="min-w-0 flex-1 bg-transparent py-2 text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                挖料
              </button>
            </div>
          </form>

          <dl className="mt-8 grid grid-cols-3 gap-4 border-t border-background-200/70 pt-6">
            {[
              { v: '1,200+', l: '独家爆料条目' },
              { v: '每日 3 篇', l: '平均加更节奏' },
              { v: '两轮', l: '独立信源核实' },
            ].map((item) => (
              <div key={item.l}>
                <dt className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                  {item.v}
                </dt>
                <dd className="mt-1 text-xs text-foreground-500">{item.l}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="animate-rise">
          <article className="overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
            <Link to={`/article/${featured.id}`} className="group block h-52 w-full overflow-hidden md:h-64">
              <img
                src={featured.cover}
                alt={`${featured.title} - 看瓜爆料独家首发`}
                className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
            </Link>
            <div className="p-5 md:p-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-primary-500 px-3 py-1 text-[11px] font-medium text-background-50">
                  今日头条爆料
                </span>
                <span className="rounded-full bg-secondary-100 px-3 py-1 text-[11px] text-secondary-900">
                  {featured.topicName}
                </span>
              </div>
              <h2 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 md:text-xl">
                <Link to={`/article/${featured.id}`} className="hover:text-primary-600">
                  {featured.title}
                </Link>
              </h2>
              <p className="mt-2 line-clamp-2 text-sm text-foreground-600">{featured.excerpt}</p>

              <div className="mt-5 rounded-md bg-background-100/70 p-3">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-foreground-700">
                  <i className="ri-fire-line text-sm text-primary-500"></i>
                  实时热榜
                </p>
                <ul className="space-y-1.5">
                  {hotRank.slice(0, 3).map((item, index) => (
                    <li key={item.id} className="flex items-start gap-2">
                      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-primary-500 text-[10px] font-bold text-background-50">
                        {index + 1}
                      </span>
                      <Link
                        to={`/article/${item.id}`}
                        className="line-clamp-1 text-xs text-foreground-700 hover:text-primary-600"
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-4 flex items-center justify-between text-xs text-foreground-500">
                <span className="inline-flex items-center gap-1">
                  <i className="ri-user-line text-[13px]"></i>
                  {featured.author}
                </span>
                <span className="inline-flex items-center gap-1 text-primary-600">
                  <i className="ri-eye-line text-[13px]"></i>
                  {formatHeat(featured.heat)} 阅读
                </span>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

export default Hero;